/**
 * statusLabels — translate-at-render helpers for stored status values.
 *
 * Stored values stay in English (e.g. "active", "draft", "completed"); only
 * the displayed label is localized via the `status.*` translation namespace.
 *
 * Usage:
 *   import { useTranslation } from 'react-i18next';
 *   import { jobStatusLabel } from '@/lib/statusLabels';
 *   const { t } = useTranslation();
 *   <Badge>{jobStatusLabel(t, job.status)}</Badge>
 */

const norm = (s) => String(s || '').trim().toLowerCase();

const labelKey = (s, fallbackKey) => {
  const key = norm(s).replace(/[\s-]+/g, '_');
  return `status.${key}` in {} ? `status.${key}` : `status.${key}`;
};

/** Generic translate-status helper — falls back to the original value if no key. */
export const statusLabel = (t, raw) => {
  if (raw == null || raw === '') return '';
  const key = labelKey(raw);
  // i18next returns the key as-is when missing (default behavior here is fine
  // because we want a visible fallback). Use a small explicit fallback.
  const translated = t(key, { defaultValue: '' });
  return translated || String(raw);
};

/** Job status — same mapping, here for callsite readability. */
export const jobStatusLabel = (t, raw) => statusLabel(t, raw);

/** Estimate status — same mapping (draft, sent, approved, declined). */
export const estimateStatusLabel = (t, raw) => statusLabel(t, raw);

/** Invoice status — same mapping (paid, unpaid, overdue, scheduled). */
export const invoiceStatusLabel = (t, raw) => statusLabel(t, raw);

/** Crew-clock live status — clocked_in / on_break / idle. */
export const clockStatusLabel = (t, raw) => statusLabel(t, raw);
